import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combines multiple class values into a single className string
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format price as Indian currency
 */
export function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Generate a slug from a string
 */
export function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Truncate a string to a specified length
 */
export function truncate(str: string, length: number): string {
  return str.length > length ? `${str.substring(0, length)}...` : str;
}

/**
 * Get category-specific placeholder images from Pexels
 */
function getCategoryPlaceholder(imageUrl: string | null): string {
  if (!imageUrl) {
    return 'https://images.pexels.com/photos/164743/pexels-photo-164743.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&dpr=1';
  }

  const url = imageUrl.toLowerCase();
  
  // Guitar placeholders
  if (url.includes('guitar') || url.includes('acoustic') || url.includes('electric')) {
    return 'https://images.pexels.com/photos/1407322/pexels-photo-1407322.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&dpr=1';
  }
  
  // Keyboard/Piano placeholders
  if (url.includes('keyboard') || url.includes('piano') || url.includes('synthesizer') || url.includes('modx')) {
    return 'https://images.pexels.com/photos/164743/pexels-photo-164743.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&dpr=1';
  }
  
  // Drum placeholders
  if (url.includes('drum') || url.includes('percussion') || url.includes('stage-custom')) {
    return 'https://images.pexels.com/photos/995301/pexels-photo-995301.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&dpr=1';
  }
  
  // Studio equipment placeholders
  if (url.includes('studio') || url.includes('audio') || url.includes('interface')) {
    return 'https://images.pexels.com/photos/744318/pexels-photo-744318.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&dpr=1';
  }
  
  // Accessories placeholder
  if (url.includes('accessories') || url.includes('cable') || url.includes('pick')) {
    return 'https://images.pexels.com/photos/1751731/pexels-photo-1751731.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&dpr=1';
  }
  
  // Default music instrument placeholder
  return 'https://images.pexels.com/photos/164743/pexels-photo-164743.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&dpr=1';
}

/**
 * Construct the full image URL using Supabase storage with brand organization
 */
export function getProductImageUrl(imageUrl: string | null): string {
  if (!imageUrl) {
    return getCategoryPlaceholder(null);
  }
  
  // If it's already a full URL, return as is
  if (imageUrl.startsWith('http')) {
    return imageUrl;
  }
  
  // Handle brand-organized storage structure
  // Expected format: brands/yamaha/guitars/acoustic/f310-tbs.jpg
  let fullUrl = '';
  
  // If the imageUrl already starts with the bucket path
  if (imageUrl.startsWith('instruments/')) {
    fullUrl = `https://daddrtnrblquorxyhbat.supabase.co/storage/v1/object/public/${imageUrl}`;
  }
  // If it starts with brands/ (new organized structure)
  else if (imageUrl.startsWith('brands/')) {
    fullUrl = `https://daddrtnrblquorxyhbat.supabase.co/storage/v1/object/public/instruments/${imageUrl}`;
  }
  // Legacy paths - maintain backward compatibility
  else if (imageUrl.startsWith('guitar/') || imageUrl.startsWith('keyboard/') || 
           imageUrl.startsWith('drums/') || imageUrl.startsWith('studio/') || 
           imageUrl.startsWith('accessories/')) {
    fullUrl = `https://daddrtnrblquorxyhbat.supabase.co/storage/v1/object/public/instruments/${imageUrl}`;
  }
  // If it's just a filename, try to construct a reasonable path
  else {
    // For specific known files, provide direct paths
    if (imageUrl === 'F310 TBS.jpg' || imageUrl.includes('f310') || imageUrl.includes('F310')) {
      fullUrl = 'https://daddrtnrblquorxyhbat.supabase.co/storage/v1/object/public/instruments/brands/yamaha/guitars/acoustic/f310-tbs.jpg';
    } else {
      // Default fallback
      fullUrl = `https://daddrtnrblquorxyhbat.supabase.co/storage/v1/object/public/instruments/guitar/${imageUrl}`;
    }
  }
  
  // URL encode the path properly
  const urlParts = fullUrl.split('/');
  const encodedParts = urlParts.map((part, index) => {
    // Don't encode the protocol and domain parts
    if (index < 3) return part;
    // Don't encode slashes
    if (part === '') return part;
    // Encode the actual path segments
    return encodeURIComponent(part);
  });
  
  return encodedParts.join('/');
}

/**
 * Get a fallback image URL for when the primary image fails to load
 */
export function getFallbackImageUrl(originalUrl: string | null): string {
  return getCategoryPlaceholder(originalUrl);
}

/**
 * Get brand logo URL from Supabase storage
 */
export function getBrandLogoUrl(logoUrl: string | null): string {
  if (!logoUrl) {
    return 'https://images.pexels.com/photos/164743/pexels-photo-164743.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&dpr=1';
  }
  
  if (logoUrl.startsWith('http')) {
    return logoUrl;
  }
  
  // Construct brand logo URL
  const baseUrl = 'https://daddrtnrblquorxyhbat.supabase.co/storage/v1/object/public/instruments/';
  return baseUrl + logoUrl;
}